<script setup lang="ts">
  import { ref, computed, type ComputedRef } from 'vue'
  import { useRouter } from 'vue-router'
  import Vue3EasyDataTable from 'vue3-easy-data-table'
  import type { Header } from 'vue3-easy-data-table'

  import CSearch from '@shared/components/ui/c-search.vue'
  import CBtn from '@shared/components/ui/c-btn.vue'
  import SvgIcon from '@shared/components/svg-icon.vue'
  import UserIcon from '@shared/components/user-icon.vue'
  import CImage from '@shared/components/ui/c-image.vue'

  import type IOrganization from '@shared/api/user-management/resources/organization/organization'
  import type IPatient from '@shared/api/patients/resources/patients/patient'

  import debounce from 'lodash.debounce'
  import { ERouterList } from '@internal/router/constants/router-list'

  const router = useRouter()

  const props = defineProps<{
    patientsList: IPatient[]
    isLoading: boolean
    organizationList: IOrganization[]
  }>()

  const emit = defineEmits(['handle-search-input', 'close', 'set-loading'])

  const searchInput = ref<string>('')

  const recentsList = ref<string[]>([])

  const handleSearchInput = () => {
    emit('set-loading', true)
    setSearchValue()
  }

  const setSearchValue = debounce(() => {
    emit('handle-search-input', searchInput.value)
    if (searchInput.value.length > 1 && !recentsList.value.includes(searchInput.value)) {
      recentsList.value.push(searchInput.value)
    }
  }, 500)

  const clearSearch = () => {
    searchInput.value = ''
    emit('set-loading', true)
    setSearchValue()
  }

  const patientFullName = ({ firstName, lastName }: { firstName: string; lastName: string }): string => {
    return `${firstName} ${lastName}`
  }

  const organizationName = (organizationId: string): string => {
    const organization = props.organizationList?.find(organization => {
      return organization.organizationId === organizationId
    })
    return organization && organization.name ? organization.name : 'Unknown organization'
  }

  const handleSelectRecent = (value: string) => {
    searchInput.value = value
    emit('set-loading', true)
    setSearchValue()
  }

  const handleClose = () => {
    emit('handle-search-input', '')
    emit('close')
  }

  const handlePatientRoute = (patient: IPatient) => {
    emit('close')
    router.push({
      name: ERouterList.PATIENT_PROFILE,
      params: {
        patientId: patient.identity.patientId,
      },
    })
  }

  const headers: ComputedRef<Header[]> = computed(() => {
    return [
      { text: '', value: 'patient' },
      { text: '', value: 'location' },
      { text: '', value: 'cta', width: 60 },
    ]
  })
</script>

<template>
  <div class="tw-z-[15] tw-bg-white tw-flex tw-flex-col tw-pb-16 tw-px-10 tw-w-[450px]" v-bind="$attrs">
    <div class="tw-flex tw-items-center tw-justify-between tw-mb-16">
      <c-search
        v-model="searchInput"
        placeholder="Start typing patient name..."
        class="tw-mt-8 tw-w-full"
        autofocus
        @on-clear="clearSearch"
        @input="handleSearchInput"
      />
      <div
        class="tw-cursor-pointer tw-ml-8 tw-w-[40px] tw-h-[40px] tw-flex tw-items-center tw-justify-center tw-mt-12"
        @click="handleClose"
      >
        <svg-icon name="rejected" size="24" class="tw-fill-primary-100" />
      </div>
    </div>
    <div v-if="props.isLoading" class="tw-w-[40px] tw-flex tw-self-center">
      <div class="tw-animate-spin-slow">
        <svg-icon name="uploading-file" class="tw-fill-secondary-200" size="40" />
      </div>
    </div>
    <vue3-easy-data-table
      v-else
      :items="patientsList"
      :rows-per-page="patientsList.length"
      :headers="headers"
      :loading="isLoading"
      hide-footer
      table-class-name="customize-table tw-overflow-auto"
      header-class-name="tw-hidden"
      header-item-class-name="tw-text-body-1"
      @click-row="handlePatientRoute"
    >
      <template #item-patient="item">
        <div class="tw-flex tw-items-center">
          <div class="tw-mr-8">
            <user-icon
              :full-name="patientFullName({ firstName: item.identity.firstName, lastName: item.identity.lastName })"
              :width="36"
              :height="36"
              :string-color="item.id"
              not-active-color="#D0DAE3"
              :is-active="true"
            />
          </div>
          <span class="tw-text-placeholder-2 tw-text-primary-300 tw-break-normal">
            {{ patientFullName({ firstName: item.identity.firstName, lastName: item.identity.lastName }) }}
          </span>
        </div>
      </template>
      <template #item-location="item">
        <div class="tw-flex tw-flex-col">
          <span class="tw-text-placeholder-2 tw-text-primary-200 tw-break-normal tw-mb-8">
            {{ item.community.name }}
          </span>
          <span class="tw-text-placeholder-2 tw-mb-2">
            {{ organizationName(item.community.organizationId) }}
          </span>
        </div>
      </template>
      <template #item-cta="item">
        <c-btn is-icon size="small" @click.stop="handlePatientRoute(item)">
          <svg-icon name="angle-arrow" size="24" class="tw-fill-primary-100" />
        </c-btn>
      </template>
      <template #empty-message>
        <div class="tw-flex tw-flex-col tw-justify-center">
          <div
            v-if="!patientsList.length && searchInput.length > 1"
            class="tw-flex tw-flex-col tw-items-center tw-py-[61px]"
          >
            <c-image name="no-results" width="132" class="tw-mb-16" />
            <span class="tw-text-button-2 tw-text-primary-300 tw-mb-16">No results for "{{ searchInput }}"</span>
            <span class="tw-text-body-1 tw-mb-16">Try searching something else</span>
            <span class="tw-text-link-1 tw-text-primary-100 tw-underline tw-cursor-pointer" @click="clearSearch">
              Clear
            </span>
          </div>
          <div v-else-if="!patientsList.length && recentsList.length > 0" class="tw-flex tw-flex-col">
            <span class="tw-text-placeholder-2 tw-text-grey-600 tw-self-start tw-py-10 tw-px-16">Recents</span>
            <div
              v-for="(item, index) in recentsList"
              :key="item"
              class="tw-flex tw-items-center tw-py-8 tw-cursor-pointer tw-px-16"
              :class="{ 'tw-border-b tw-border-grey-400': index + 1 !== recentsList.length }"
              @click="handleSelectRecent(item)"
            >
              <svg-icon name="search" class="tw-fill-grey-600 tw-mr-8" />
              <span class="tw-text-subtitle-1 tw-text-grey-800">
                {{ item }}
              </span>
            </div>
          </div>
          <div v-else-if="!patientsList.length" class="tw-flex tw-flex-col tw-items-center tw-py-[61px]">
            <c-image name="search" width="132" class="tw-mb-16" />
            <span class="tw-text-button-2 tw-text-primary-300 tw-mb-16">Start typing what you are searching for</span>
            <span class="tw-text-body-1">Results will appear here</span>
          </div>
        </div>
      </template>
    </vue3-easy-data-table>
  </div>
</template>

<style lang="scss">
  @import 'vue3-easy-data-table/dist/style.css';
</style>
